import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  ChatCircle,
  GithubLogo,
  TwitterLogo,
  LinkedinLogo,
  BookOpen,
  Users,
} from "@phosphor-icons/react";
import { useFeature } from "../context/FeatureFlagContext";

const CHANNELS: {
  icon: typeof ChatCircle;
  title: string;
  description: string;
  cta: string;
  href: string;
  isRoute?: boolean;
  flag?: "BLOG";
}[] = [
  {
    icon: ChatCircle,
    title: "Discussion Forum",
    description:
      "Ask questions, share tips, and help other Opero users. Our team actively participates.",
    cta: "Join the conversation",
    href: "#",
  },
  {
    icon: GithubLogo,
    title: "GitHub",
    description:
      "Report issues, request features, and follow along with our open-source projects.",
    cta: "View on GitHub",
    href: "#",
  },
  {
    icon: TwitterLogo,
    title: "Twitter / X",
    description:
      "Follow us for product updates, tips, and behind-the-scenes looks at what we're building.",
    cta: "Follow @OperoHQ",
    href: "#",
  },
  {
    icon: LinkedinLogo,
    title: "LinkedIn",
    description:
      "Connect with us for company news, thought leadership, and career opportunities.",
    cta: "Follow on LinkedIn",
    href: "#",
  },
  {
    icon: BookOpen,
    title: "Blog",
    description:
      "Deep dives into productivity, team management, and how to get the most out of Opero.",
    cta: "Read the blog",
    href: "/blog",
    isRoute: true,
    flag: "BLOG",
  },
  {
    icon: Users,
    title: "Events & Webinars",
    description:
      "Join our live sessions to learn new features, best practices, and connect with the team.",
    cta: "View upcoming events",
    href: "#",
  },
];

export function CommunityPage() {
  const blogEnabled = useFeature("BLOG");
  const visibleChannels = CHANNELS.filter(
    (channel) => !channel.flag || (channel.flag === "BLOG" && blogEnabled),
  );
  return (
    <main className="pt-28 pb-20">
      <div className="mx-auto max-w-4xl px-6">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm font-medium text-slate-500 dark:text-slate-400 hover:text-primary-500 dark:hover:text-primary-400 transition-colors"
        >
          <ArrowLeft size={16} />
          Back to Home
        </Link>

        <div className="mt-8 text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-primary-500/20 bg-primary-500/5 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-primary-500 dark:border-primary-400/20 dark:bg-primary-400/5 dark:text-primary-300">
            Community
          </span>
          <h1 className="mt-6 text-3xl font-bold tracking-tight text-slate-900 dark:text-white sm:text-4xl">
            Connect, learn, and grow{" "}
            <span className="gradient-text">together</span>
          </h1>
          <p className="mt-4 text-lg text-slate-600 dark:text-slate-300 max-w-xl mx-auto">
            Join thousands of teams and individuals who share ideas, get help,
            and shape the future of Opero.
          </p>
        </div>

        <div className="mt-14 grid gap-5 sm:grid-cols-2">
          {visibleChannels.map((channel) => (
            <div
              key={channel.title}
              className="group rounded-2xl border border-slate-200/60 dark:border-slate-800/60 bg-white dark:bg-slate-900 p-7 transition-all duration-300 hover:border-primary-500/30 dark:hover:border-primary-400/20 hover:shadow-lg"
            >
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-primary-500/10 to-primary-400/10 dark:from-primary-500/20 dark:to-primary-400/20">
                <channel.icon
                  size={22}
                  weight="duotone"
                  className="text-primary-500 dark:text-primary-400"
                />
              </div>
              <h3 className="mt-4 text-base font-semibold text-slate-900 dark:text-white">
                {channel.title}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600 dark:text-slate-400">
                {channel.description}
              </p>
              {channel.isRoute ? (
                <Link
                  to={channel.href}
                  className="mt-4 inline-flex items-center gap-1 text-xs font-medium text-primary-500 dark:text-primary-400 group-hover:gap-2 transition-all"
                >
                  {channel.cta}
                  <ArrowRight size={12} weight="bold" />
                </Link>
              ) : (
                <a
                  href={channel.href}
                  className="mt-4 inline-flex items-center gap-1 text-xs font-medium text-primary-500 dark:text-primary-400 group-hover:gap-2 transition-all"
                >
                  {channel.cta}
                  <ArrowRight size={12} weight="bold" />
                </a>
              )}
            </div>
          ))}
        </div>
      </div>
    </main>
  );
}
